import type {
  PDFPageProxy,
  TextContent,
} from "pdfjs-dist/types/src/display/api"
import type { IHighlight } from "react-pdf-highlighter"

type RawTextItem = TextContent["items"][number]
type TextItem = Extract<
  RawTextItem,
  {
    str: string
    transform: number[]
    width: number
    height: number
  }
>

const isTextItem = (item: RawTextItem): item is TextItem =>
  typeof (item as { str?: unknown }).str === "string" &&
  Array.isArray((item as { transform?: unknown }).transform)

export interface TextMatch {
  items: TextItem[]
  startIndex: number
  endIndex: number
  rects: DOMRect[]
}

export interface PdfViewportInfo {
  width: number
  height: number
  convertToViewportRectangle: (
    rect: [number, number, number, number],
  ) => [number, number, number, number]
}

interface FindTextOptions {
  alreadyNormalized?: boolean
}

export function normalizeText(text: string): string {
  if (!text) {
    return ""
  }

  return (
    text
      .replace(/[\u2010-\u2015]/g, "-")
      .replace(/\u00ad/g, "")
      .replace(/-\s*[\r\n]+\s*/g, "")
      .replace(/-\s*$/g, "")
      .replace(/-/g, " ")
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\u00A0/g, " ")
      .replace(/[\r\n\t]/g, " ")
      // Remove special symbols (e.g., �, punctuation) so matching focuses on text.
      .replace(/[^a-zA-Z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase()
  )
}

export function findTextInPage(
  searchText: string,
  textContent: TextContent,
  _page: PDFPageProxy,
  options: FindTextOptions = {},
): TextMatch | null {
  const normalizedSearch = options.alreadyNormalized
    ? searchText
    : normalizeText(searchText)

  if (!normalizedSearch) {
    return null
  }

  const items: TextItem[] = (textContent.items ?? []).filter(isTextItem)
  if (!items.length) {
    return null
  }

  const normalizedItems = items.map((item) => normalizeText(item.str))

  const directMatch = findDirectItemMatch(
    normalizedSearch,
    items,
    normalizedItems,
  )
  if (directMatch) {
    return directMatch
  }

  // PDF.js text selection can omit spaces at item boundaries ("verifiedby",
  // "Ref2042") even though extracted items and the rendered PDF show a break.
  // Match the complete character sequence before allowing a partial token match.
  const compactMatch = findDirectItemMatch(
    normalizedSearch.replace(/\s/g, ""),
    items,
    normalizedItems.map((text) => text.replace(/\s/g, "")),
    false,
  )
  if (compactMatch) {
    return compactMatch
  }

  return findApproximateItemMatch(normalizedSearch, items, normalizedItems)
}

function findDirectItemMatch(
  normalizedSearch: string,
  items: TextItem[],
  normalizedItems: string[],
  joinWithSpace = true,
): TextMatch | null {
  let aggregated = ""
  const charToItemIndex: number[] = []

  normalizedItems.forEach((text, idx) => {
    if (!text) {
      return
    }

    if (aggregated.length > 0 && joinWithSpace) {
      aggregated += " "
      charToItemIndex.push(idx)
    }

    for (const char of text) {
      aggregated += char
      charToItemIndex.push(idx)
    }
  })

  if (!aggregated) {
    return null
  }

  const start = aggregated.indexOf(normalizedSearch)
  if (start === -1) {
    return null
  }

  const end = start + normalizedSearch.length - 1
  const startItemIndex = charToItemIndex[start]
  const endItemIndex = charToItemIndex[end]

  return buildMatchFromRange(items, startItemIndex, endItemIndex)
}

function findApproximateItemMatch(
  normalizedSearch: string,
  items: TextItem[],
  normalizedItems: string[],
): TextMatch | null {
  const searchTokens = normalizedSearch.split(" ").filter(Boolean)
  if (!searchTokens.length) {
    return null
  }

  const tokenStream: { token: string; itemIndex: number }[] = []
  normalizedItems.forEach((text, itemIndex) => {
    const tokens = text.split(" ").filter(Boolean)
    tokens.forEach((token) => {
      tokenStream.push({ token, itemIndex })
    })
  })

  if (!tokenStream.length) {
    return null
  }

  const maxWindowTokens = Math.max(
    searchTokens.length + 10,
    searchTokens.length * 2,
  )
  const bestMatch = {
    score: 0,
    startItemIndex: 0,
    endItemIndex: 0,
    matches: 0,
  }

  for (
    let startTokenIdx = 0;
    startTokenIdx < tokenStream.length;
    startTokenIdx++
  ) {
    let searchIdx = 0
    let pageIdx = startTokenIdx
    let matches = 0
    let lastItemIndex = tokenStream[startTokenIdx].itemIndex
    let tokensCompared = 0

    while (
      searchIdx < searchTokens.length &&
      pageIdx < tokenStream.length &&
      tokensCompared <= maxWindowTokens
    ) {
      const pageToken = tokenStream[pageIdx].token
      const targetToken = searchTokens[searchIdx]

      if (pageToken === targetToken) {
        matches++
        lastItemIndex = Math.max(lastItemIndex, tokenStream[pageIdx].itemIndex)
        pageIdx++
        searchIdx++
      } else {
        searchIdx++
      }

      tokensCompared++
    }

    if (matches === 0) {
      continue
    }

    const score = matches / searchTokens.length
    if (score > bestMatch.score) {
      bestMatch.score = score
      bestMatch.startItemIndex = tokenStream[startTokenIdx].itemIndex
      bestMatch.endItemIndex = lastItemIndex
      bestMatch.matches = matches
    }

    if (bestMatch.score === 1) {
      break
    }
  }

  if (bestMatch.matches === 0) {
    return null
  }

  const minimumScore = searchTokens.length > 30 ? 0.5 : 0.7
  const minimumPartialMatches = Math.max(
    2,
    Math.ceil(searchTokens.length * 0.3),
  )

  if (
    bestMatch.score < minimumScore &&
    bestMatch.matches < minimumPartialMatches
  ) {
    return null
  }

  return buildMatchFromRange(
    items,
    bestMatch.startItemIndex,
    Math.max(bestMatch.startItemIndex, bestMatch.endItemIndex),
  )
}

function buildMatchFromRange(
  items: TextItem[],
  startIndex?: number,
  endIndex?: number,
): TextMatch | null {
  if (
    startIndex === undefined ||
    endIndex === undefined ||
    startIndex < 0 ||
    endIndex < 0
  ) {
    return null
  }

  const safeStart = Math.max(0, Math.min(startIndex, items.length - 1))
  const safeEnd = Math.max(safeStart, Math.min(endIndex, items.length - 1))
  const matchedItems = items.slice(safeStart, safeEnd + 1)

  if (!matchedItems.length) {
    return null
  }

  const rects = matchedItems.map((item) => getItemRect(item))

  return {
    items: matchedItems,
    startIndex: safeStart,
    endIndex: safeEnd,
    rects,
  }
}

function getItemRect(item: TextItem): DOMRect {
  const transform = item.transform
  const x = transform[4]
  // pdf.js positions text using the baseline; derive the actual top so the
  // highlight hugs the glyph and then extend a little below for descenders.
  const baselineY = transform[5]
  const width = item.width
  const height = item.height
  const descenderPad = height * 0.25
  const y = baselineY - descenderPad

  return new DOMRect(x, y, width, height)
}

export function createHighlightFromMatch(
  match: TextMatch,
  pageNumber: number,
  viewport: PdfViewportInfo,
): IHighlight {
  const { rects, items } = match
  const viewportRects = rects.map((rect) => mapRectToViewport(rect, viewport))

  const boundingRect = viewportRects.reduce(
    (acc, rect) => ({
      x1: Math.min(acc.x1, rect.x1),
      y1: Math.min(acc.y1, rect.y1),
      x2: Math.max(acc.x2, rect.x2),
      y2: Math.max(acc.y2, rect.y2),
      width: viewport.width,
      height: viewport.height,
      pageNumber,
    }),
    {
      x1: Infinity,
      y1: Infinity,
      x2: -Infinity,
      y2: -Infinity,
      width: viewport.width,
      height: viewport.height,
      pageNumber,
    },
  )

  const resolvedBoundingRect = viewportRects.length
    ? boundingRect
    : {
        x1: 0,
        y1: 0,
        x2: 0,
        y2: 0,
        width: viewport.width,
        height: viewport.height,
        pageNumber,
      }

  const scaledRects = viewportRects.map((rect) => ({
    ...rect,
    width: viewport.width,
    height: viewport.height,
    pageNumber,
  }))

  const matchedText = items.map((item) => item.str).join(" ")

  return {
    id: `highlight-${Date.now()}`,
    content: {
      text: matchedText,
    },
    position: {
      pageNumber,
      boundingRect: resolvedBoundingRect,
      rects: scaledRects,
    },
    comment: {
      text: "Match found",
      emoji: "",
    },
  }
}

function mapRectToViewport(rect: DOMRect, viewport: PdfViewportInfo) {
  const [x1, y1, x2, y2] = viewport.convertToViewportRectangle([
    rect.x,
    rect.y,
    rect.x + rect.width,
    rect.y + rect.height,
  ])

  const left = Math.min(x1, x2)
  const top = Math.min(y1, y2)
  const right = Math.max(x1, x2)
  const bottom = Math.max(y1, y2)

  return {
    x1: left,
    y1: top,
    x2: right,
    y2: bottom,
  }
}

export function calculateSimilarity(str1: string, str2: string): number {
  const normalized1 = normalizeText(str1)
  const normalized2 = normalizeText(str2)

  if (normalized1 === normalized2) return 1.0

  const longer =
    normalized1.length > normalized2.length ? normalized1 : normalized2
  const shorter =
    normalized1.length > normalized2.length ? normalized2 : normalized1

  if (longer.length === 0) return 1.0

  const editDistance = getLevenshteinDistance(shorter, longer)
  return (longer.length - editDistance) / longer.length
}

function getLevenshteinDistance(str1: string, str2: string): number {
  const matrix: number[][] = []

  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i]
  }

  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j
  }

  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1]
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j] + 1, // deletion
        )
      }
    }
  }

  return matrix[str2.length][str1.length]
}
